import { formattedMessageSentAt } from "@circle-react/helpers/dateTimeHelpers/formattedMessageSentAt";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Typography } from "@circleco/compass/components/Typography";

import { ContentBody } from "@/react/components/Chat/ChatWindowV2/MessageItem/ContentBody";
import { useRedirectToMessage } from "@/react/components/Chat/ChatWindowV2/MessageItem/hooks/useRedirectToMessage";
import { useRailbar } from "@/react/hooks/chatsV2/useRailbar";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import type { ChatRoomMessageSearchResult } from "@/react/types/Chat/ChatRoomMessage";

export const MessageResultItem = ({
  message,
  activeMessageId,
  setActiveMessageId,
  onNavigate,
}: {
  activeMessageId: number | null;
  message: ChatRoomMessageSearchResult;
  onNavigate?: () => void;
  setActiveMessageId: (id: number) => void;
}) => {
  const { sender, sent_at: sentAt } = message;
  const { day, time } = formattedMessageSentAt(sentAt);
  const { redirectToMessage } = useRedirectToMessage();
  const {
    toggleShowEmbeddedRailBar,
    shouldShowEmbeddedRailBar,
    closeThreadsRailbar,
  } = useRailbar();
  const isSmScreen = useSmOrMdOrLgScreenMediaQuery();

  const isToday = day === "Today";
  const getMessageTime = () => (isToday ? time : day);

  const onClick = () => {
    if (shouldShowEmbeddedRailBar) {
      toggleShowEmbeddedRailBar();
    }
    if (isSmScreen) {
      closeThreadsRailbar();
    }
    setActiveMessageId(message.id);
    redirectToMessage(message);
    onNavigate?.();
  };

  return (
    <button
      type="button"
      key={message.id}
      className={classNames(
        "bg-primary hover:border-hover hover:bg-hover w-full cursor-pointer rounded-lg border px-4 py-3 text-left",
        activeMessageId === message.id
          ? "border-hover bg-active"
          : "border-primary",
      )}
      onClick={onClick}
    >
      <div className="mb-1 flex items-center gap-2">
        <div className="self-start">
          <Avatar src={sender.avatar_url} name={sender.name} size="sm" />
        </div>
        <Typography variant="heading-sm">{sender.name}</Typography>
        <Typography variant="caption" color="tertiary">
          {getMessageTime()}
        </Typography>
      </div>
      <ContentBody message={message} shouldShowPreviewBlocks={false} />
    </button>
  );
};
